import { SubscriptionPlans } from '../../../../../../user/user/user.model';
import { MapModalData } from '../../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { ROUTER_NAVIGATE } from '../../../../../../user/map-redirect-modal/map-redirect-modal-text';
// Type-only: keeps this leaf util free of a runtime edge into the access
// service and the modal service, both of which pull in dialog components.
import type {
  FeatureType,
  UserAccessService,
} from '../../../../../../user/access/user-access.service';
import type { ModalService } from '../../../../../../shared/services/modal.service';

/** Reported with `sign_up` when a locked map indicator opened the gate. */
export const LOCKED_INDICATOR_PLACEMENT = 'map-locked-indicator';

/**
 * True when a guest is looking at an indicator that signing up would NOT
 * unlock: it is behind the paywall, not merely behind an account.
 *
 * The guest gate below promises free access, and for most locked indicators
 * that promise is true - the trial list is much longer than the guest one.
 * But a good part of the map is subscription-only, and offering those an
 * account sends the visitor through sign-up to the very same padlock. Those
 * get the plans modal instead, the one the popup's "Get full access"
 * CTA opens.
 *
 * A missing feature constant (the comparison table renders tooltips without
 * one) can't be classified, so it keeps the sign-up prompt.
 */
export function signUpWouldNotUnlock(
  feature: FeatureType | undefined,
  accessService: UserAccessService
): boolean {
  return (
    !!feature &&
    accessService.isUnauthorized &&
    accessService.isBySubscription(feature)
  );
}

/**
 * A locked indicator was clicked, from the map popup, the layers menu or a
 * chart block. Gives the one answer that fits the visitor's actual state:
 *
 * - guest, and an account would unlock it -> the sign-up modal itself. There
 *   used to be an "Unlock This with a Free Trial" prompt in front of it whose
 *   only button opened this very modal; the extra click said less than the
 *   modal it led to.
 * - guest, but the indicator is paywalled -> the plans modal, see above.
 * - signed in -> the subscription upsell, priced off the plans themselves.
 */
export function openLockedFeaturePrompt(
  feature: FeatureType | undefined,
  featureDisplayName: string | undefined,
  accessService: UserAccessService,
  modalService: ModalService
): void {
  const displayName = featureDisplayName?.trim() || 'This data';

  if (accessService.isUnauthorized) {
    if (signUpWouldNotUnlock(feature, accessService)) {
      modalService.openSubscriptionsModal('all_packages');
      return;
    }

    modalService.requestAuth({
      headerText: `${displayName} is free with an account`,
      subHeaderText:
        'Signing up takes a few seconds - no credit card, nothing to pay.',
      placement: LOCKED_INDICATOR_PLACEMENT,
    });
    return;
  }

  modalService.openModal(subscriptionUpsellData(displayName));
}

/**
 * The cheapest user-facing plan's monthly price ('$29.95'), or undefined.
 *
 * Read off the plans themselves rather than written down: the number was
 * hardcoded once and went stale the moment subscriptions were re-priced.
 */
export function lowestMonthlyPlanPrice(): string | undefined {
  const userFacingPlans: SubscriptionPlans[] = [
    SubscriptionPlans.HOME_SEARCH,
    SubscriptionPlans.REAL_ESTATE,
    SubscriptionPlans.SITE_SELECTION,
  ];

  // Strips everything but digits and the decimal point, so a thousands
  // separator ('$2,499.95', which ENTERPRISE already carries) sorts as 2499
  // and not as 2 the way a bare '$' strip would.
  const amountOf = (price: string): number =>
    parseFloat(price.replace(/[^\d.]/g, ''));

  return userFacingPlans
    .map((planKey) => SubscriptionPlans.description[planKey].priceMonthly)
    .filter((price): price is string => !!price)
    .sort((a, b) => amountOf(a) - amountOf(b))[0];
}

/**
 * Every subscription unlocks every indicator, so the only thing left to say
 * is what the cheapest one costs.
 */
function subscriptionUpsellData(featureDisplayName: string): MapModalData {
  const lowestPrice = lowestMonthlyPlanPrice();

  return {
    headerText: `Unlock ${featureDisplayName}`,
    buttonText: 'Get started',
    URLtoGo: '/user/subscription',
    actionType: ROUTER_NAVIGATE,
    hasBackdrop: true,
    gaEventOnButtonClick: 'get_started_locked_param',
    mainText: `${featureDisplayName} is available with any subscription.`,
    additionalText: lowestPrice
      ? `Get full access to all map data starting ${lowestPrice}/month.`
      : `Get full access to all map data with any subscription.`,
  };
}

/**
 * Why an indicator's data is hidden and what the visitor can do about it. Two
 * different gates sit in front of the map data and they ask for very
 * different things, so a guest is told which indicators a free account opens
 * and which ones are actually being sold. See UserAccessService.isBySubscription.
 *
 * Lives here rather than next to the popup that first needed it: the right
 * menu's locked charts make the same two promises, and the wording has to be
 * the one wording - a visitor meets both surfaces in the same session.
 */
export interface LockCopy {
  /** The free-with-an-account gate. Painted brand blue, not paywall violet. */
  free: boolean;
  /** Short label for the gate itself. Pairs with `cta` on a real button. */
  note: string;
  /** The word on the control that opens the prompt. */
  cta: string;
  /**
   * The gate and the way through it in one sentence, for the places where the
   * ask is the whole row rather than a label beside a button. Split across two
   * texts there, it read as two separate offers on one line.
   */
  phrase: string;
  /** Full phrase for screen readers, prefixed to the indicator's name. */
  aria: string;
}

export const ACCOUNT_LOCK: LockCopy = {
  free: true,
  note: 'Free with an account',
  cta: 'Sign up',
  phrase: "Sign up - it's free",
  aria: 'Sign up free to see',
};

export const PLAN_LOCK: LockCopy = {
  free: false,
  note: 'Included in a paid plan',
  cta: 'Unlock',
  phrase: 'Unlock with a paid plan',
  aria: 'Unlock',
};

/**
 * Which gate `feature` sits behind, or null when the visitor may see it.
 * Only a guest can meet the account gate: for anyone signed in without a plan
 * the trial list is exactly what they already have, so every padlock they see
 * is the paid one.
 */
export function lockCopyFor(
  feature: FeatureType,
  accessService: UserAccessService
): LockCopy | null {
  if (accessService.userHasAccess(feature)) return null;

  return accessService.isBySubscription(feature) ? PLAN_LOCK : ACCOUNT_LOCK;
}
